import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Trash2 } from 'lucide-react'
import { useContact, useUpdateContact, useDeleteContact } from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { parseUTCDate } from '@/lib/utils'
import { formatContactRole } from '@/lib/contact-roles'
import { toast } from 'sonner'
import { usePageTitle } from '@/lib/page-title'

type ContactKey = 'name' | 'email'

const CONTACT_FIELDS: ReadonlyArray<InlineGroupField<ContactKey>> = [
  { key: 'email', label: 'Email', autoComplete: 'email' },
  { key: 'name', label: 'Name', autoComplete: 'name' },
]

interface ContactDetailPageProps {
  contactId: number
}

export function ContactDetailPage({ contactId }: ContactDetailPageProps) {
  const navigate = useNavigate()
  const { data: contact, isLoading } = useContact(contactId)
  usePageTitle(contact && (contact.name || contact.email))
  const updateContact = useUpdateContact(contactId)
  const deleteContact = useDeleteContact(contactId)

  const [editing, setEditing] = useState(false)

  const handleSave = (next: Record<ContactKey, string>) => {
    updateContact.mutate(
      {
        name: next.name.trim() || undefined,
        email: next.email.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Contact updated')
          setEditing(false)
        },
        onError: () => toast.error('Failed to update contact'),
      },
    )
  }

  const handleDelete = () => {
    deleteContact.mutate(undefined, {
      onSuccess: () => {
        toast.success('Contact deleted')
        navigate({ to: '/contacts' })
      },
      onError: () => toast.error('Failed to delete contact'),
    })
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!contact) {
    return (
      <div className="mx-auto max-w-7xl">
        <p className="text-muted-foreground">Contact not found</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <BackButton fallback={{ to: '/contacts' }} fallbackLabel="Back to Contacts" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center">
          <h1 className="break-words text-2xl font-bold tracking-tight">
            {contact.name || contact.email}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LoadingButton
            variant="destructive"
            size="sm"
            loading={deleteContact.isPending}
            loadingText="Deleting..."
            onClick={handleDelete}
          >
            <Trash2 className="mr-2 h-3 w-3" />
            Delete
          </LoadingButton>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact Info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <InlineFieldGroup
            label="Contact info"
            fields={CONTACT_FIELDS}
            values={{ name: contact.name || '', email: contact.email || '' }}
            editing={editing}
            onEditingChange={setEditing}
            pending={updateContact.isPending}
            onSave={handleSave}
          >
            <span className="block space-y-3">
              <span className="block">Email: <span className="font-medium">{contact.email}</span></span>
              <span className="block">
                Name:{' '}
                {contact.name ? (
                  <span className="font-medium">{contact.name}</span>
                ) : (
                  <span className="text-muted-foreground">None</span>
                )}
              </span>
            </span>
          </InlineFieldGroup>
          <p className="text-muted-foreground">
            Created: {parseUTCDate(contact.created_at).toLocaleDateString()}
          </p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h3 className="text-base font-semibold">
          Linked Manufacturers ({contact.manufacturer_links?.length ?? 0})
        </h3>
        {/* Desktop table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Linked</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contact.manufacturer_links?.length ? (
                contact.manufacturer_links.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell>
                      <Link
                        to="/manufacturers/$manuId"
                        params={{ manuId: link.manufacturer.uuid }}
                        className="font-medium text-primary hover:underline"
                      >
                        {link.manufacturer.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{formatContactRole(link.role)}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {parseUTCDate(link.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    Not linked to any manufacturers
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobile cards */}
        <div className="space-y-2 md:hidden">
          {contact.manufacturer_links?.length ? (
            contact.manufacturer_links.map((link) => (
              <div key={link.id} className="rounded-lg border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to="/manufacturers/$manuId"
                    params={{ manuId: link.manufacturer.uuid }}
                    className="min-w-0 flex-1 break-words text-sm font-medium text-primary hover:underline"
                  >
                    {link.manufacturer.name}
                  </Link>
                  <Badge variant="secondary" className="shrink-0">
                    {formatContactRole(link.role)}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Linked {parseUTCDate(link.created_at).toLocaleDateString()}
                </p>
              </div>
            ))
          ) : (
            <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
              Not linked to any manufacturers
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
